import {
  defaultShouldDehydrateQuery,
  QueryClient,
} from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { isRefusal } from "@/client/lib/api";
import { KEEP_FOR } from "@/client/lib/persist";

// The working set is fetched once and filtered in memory, so a new filter is never a new request.
// Freshness comes from the delta endpoint (`sync.ts`), not refetching.
export function createQueryClient(): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60_000,
        // A query dropped from memory is dropped from the next save, so it lives as long as a save.
        gcTime: KEEP_FOR,
        refetchOnWindowFocus: false,
        // A refusal would get the same answer again; a request that never arrived gets one more try.
        retry: (attempt, failure) => !isRefusal(failure) && attempt < 1,
      },
      dehydrate: {
        // Never on disk: pay rates; the payer's card and address on Billing; the session (a
        // restored one would pass a guard for a removed person); invitation and join-link tokens,
        // which admit their holder into a company; an unsubscribe token, which changes its
        // person's email.
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) &&
          query.queryKey[0] !== "pay" &&
          query.queryKey[0] !== "billing" &&
          query.queryKey[0] !== "session" &&
          query.queryKey[0] !== "join" &&
          query.queryKey[0] !== "unsubscribe" &&
          !(query.queryKey[0] === "me" && query.queryKey[1] === "invitations"),
        shouldDehydrateMutation: () => false,
      },
    },
  });
  // Attendance and leave are read again on focus (`docs/spec/attendance.md`, A stale screen) and
  // once on every visit, so a decision or a check-in made elsewhere wins over the saved copy. A
  // read the visit's own loader made is not repeated when its screen mounts.
  for (const domain of ["attendance", "leave"]) {
    queryClient.setQueryDefaults([domain], {
      staleTime: (query) =>
        query.state.dataUpdatedAt >= visitFrom ? Number.POSITIVE_INFINITY : 0,
      refetchOnWindowFocus: "always",
    });
  }
  return queryClient;
}

/** When this page loaded; a copy restored from disk is older. */
export const PAGE_LOADED = Date.now();

/** When the current visit began; a copy restored from disk is older than the page. */
let visitFrom = Date.now();

/** Each navigation is a visit. */
export function followVisits(router: AnyRouter): void {
  router.subscribe("onBeforeNavigate", () => {
    visitFrom = Date.now();
  });
}
